/** @format */

import { Transition } from "@headlessui/react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useLiveRun } from "@/api/runsApi";
import type { RunDetail } from "@/lib/schemas/apiSchemas";
import { FLAGS } from "@/utils/constants";

const MOCK_LIVE_RUNS: RunDetail[] = [
  {
    id: "mock-live-1",
    track: "Daytona",
    name: "Daytona 500",
    flag: 1,
    lapNumber: 87,
    totalLaps: 200,
    currentStage: "Stage 2",
    stageRemaining: 13,
    lapsRemaining: 113,
  } as RunDetail,
];

export const LiveRaceBanner = () => {
  const { data: realLiveRuns = [] } = useLiveRun();
  const [mockActive, setMockActive] = useState(false);

  const toggleMock = useCallback(() => {
    setMockActive(prev => !prev);
  }, []);

  // Dev: expose toggle on window for manual testing.
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    (window as unknown as Record<string, unknown>).__galaxie_mock_live = toggleMock;
    return () => {
      delete (window as unknown as Record<string, unknown>).__galaxie_mock_live;
    };
  }, [toggleMock]);

  // Dev shortcut: Ctrl+Shift+L to toggle a mock live race.
  useHotkeys(
    "ctrl+shift+l",
    () => toggleMock(),
    { enabled: import.meta.env.DEV, enableOnFormTags: true, preventDefault: true },
    [toggleMock]
  );

  const liveRuns = mockActive ? MOCK_LIVE_RUNS : realLiveRuns;

  if (!liveRuns?.length) {
    return null;
  }

  return (
    <Transition
      as="div"
      show={liveRuns.length > 0}
      enter="transition-all duration-500"
      enterFrom="opacity-0 -translate-y-full"
      enterTo="opacity-100 translate-y-0"
      leave="transition-all duration-500"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 -translate-y-full"
    >
      <div className="flex gap-3 overflow-x-auto scrollbar-hide px-2 py-1">
        {liveRuns.map(run => (
          <Link
            key={run.id}
            to="/run/$runID"
            params={{ runID: run.id }}
            data-flag={run.flag}
            className="
              flex-shrink-0 min-w-[260px] rounded-xl border border-slate-700/80
              px-3 shadow-sm backdrop-blur
              transition-all duration-300 hover:-translate-y-0.5
              hover:border-cyan-400/70 hover:shadow-cyan-400/10
            "
            aria-label={`View live race: ${run.name} at ${run.track}`}
          >
            <div className="flex items-start gap-2 min-w-0 bg-black/70 rounded-xl px-4 py-1 my-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="font-semibold text-sm md:text-base truncate text-white">
                  {run.track} · {run.name}
                </div>
                <div className="text-xs md:text-sm text-slate-200 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <span className="text-slate-200">
                      {FLAGS[run.flag] || "Racing"}
                    </span>
                  </span>
                  <span>
                    Lap {run.lapNumber} / {run.totalLaps}
                  </span>
                  {run.currentStage && run.stageRemaining !== null && (
                    <span>
                      {run.currentStage} · {run.stageRemaining} laps left
                    </span>
                  )}
                  {run.lapsRemaining !== undefined && (
                    <span className="text-slate-200">{run.lapsRemaining} to go</span>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Transition>
  );
};
